import React, {useState, useEffect} from 'react';
import EditModal from "components/Modal/EditModal/EditModal";
import {FlightSelectSearchable, FlightButton} from "@flybits/webapp-design-system-react";
import './ScheduleEditModal.scss';
import {
  getTimezoneList,
  getDuration,
  convertDateToTimezone,
  transformScheduleData,
  cleanupTimezone
} from 'helpers/templated-experience.helper';
import 'date-fns';
import DateFnsUtils from '@date-io/date-fns';
import {
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import moment from 'moment';
import { TExperienceInstance } from 'interface/templated-experience/templated-experience.interface';
import { FlightDateTimePicker } from '@flybits/webapp-design-system-react';
import momentTZ from "moment-timezone";
import {useSelector} from "react-redux";
import { isEmpty } from 'lodash';



interface IProps {
  title: string,
  instance: TExperienceInstance | object,
  isVisible: boolean,
  primaryButton: {
    name: string,
    onClick: (scheduleData:scheduleDataType) => void,
    disabled: boolean,
    loading: boolean,
  },
  secondaryButton?: {
    name: string,
    onClick: (scheduleData:scheduleDataType) => void,
    disabled: boolean,
  } | undefined,
  cancelButton?: {
    name: string,
    onClick: () => void,
  } | undefined,
  schedule: any
}

export interface scheduleDataType {
  start: Date | null,
  end: Date | null,
  timezone: {
    key: number,
    name: string,
    offset: number,
  },
  runtime: string,
  minDate: Date,
}


export default function ScheduleEditModal(props: IProps) {
  const reduxTemplatedExperienceState = useSelector((state: any) => state.templatedExperience);
  const experience = !isEmpty(reduxTemplatedExperienceState.template) ? reduxTemplatedExperienceState.template : reduxTemplatedExperienceState.instance;
  const scheduleState = props.schedule;
  const [scheduleData, setScheduleData] = useState<scheduleDataType>({
    start: null,
    end: null,
    timezone: {
      key: 0,
      name: '',
      offset: 0,
    },
    runtime: 'None',
    minDate: new Date(),
  });
  const [timezones, setTimezones] = useState<any>([]);
  const [startDateExists, setStartDateExists] = useState<boolean>(false);
  const [endDateExists, setEndDateExists] = useState<boolean>(false);
  const dateFormat = "MMM dd yyyy, hh:mma";
  const transformedSchedule = transformScheduleData(scheduleData);
  const convertStart = convertDateToTimezone(transformedSchedule.start!!, scheduleData.timezone.name);
  const convertEnd = convertDateToTimezone(transformedSchedule.end!!, scheduleData.timezone.name);
  const manualActivation = reduxTemplatedExperienceState?.instance?.activation?.manualActivationAt;
  const autoActivation = reduxTemplatedExperienceState?.instance?.activation?.automaticActivationAt;

  function handleTimezoneSelect(option: any) {
    let trSchedule = transformScheduleData({...scheduleData, timezone: option});
    const minDateFormat = momentTZ().tz(cleanupTimezone(trSchedule?.timezone!!)).format('YYYY-MM-DD HH:mm:ss');
    const minDate = new Date(minDateFormat);
    const minDateUnix = moment(minDate).unix();
    let start = scheduleData.start;
    let startUnix = moment(start!!).unix();
    if(minDateUnix > startUnix) {
      start = new Date((minDateUnix+300) * 1000);
    }
    setScheduleData({...scheduleData, start, timezone: {...option}, minDate});
  }

  function handleSearch(searchZone: string) {
    setTimezones(getTimezoneList(searchZone));
  }

  function changeStartDate(date: any | null) {
    let selectedDate = date;
    let selectedDateUnix = moment(date).unix();
    let startDate = scheduleData.start || date;
    let startDateUnix = moment(startDate).unix();
    let endDate = scheduleData.end || date;
    let endDateUnix = moment(endDate).unix();
    let minDateUnix = moment(scheduleData.minDate).unix();
    if(minDateUnix >= selectedDateUnix) {
      selectedDateUnix = minDateUnix + 300;
      selectedDate = new Date(selectedDateUnix * 1000);
    }
    if(selectedDateUnix >= endDateUnix) {
      endDate = new Date((selectedDateUnix + 300)*1000);
    }
    if(selectedDateUnix !== startDateUnix) {
      selectedDate = new Date(selectedDateUnix*1000);
    }
    let runtime:any = getDuration(selectedDate, endDate);
    if(endDateExists) {
      setScheduleData({...scheduleData, start: selectedDate, runtime, end: endDate});
    } else {
      runtime = 'Ongoing';
      setScheduleData({...scheduleData, start: selectedDate, runtime});
    }
  }

  function changeEndDate(date: any | null) {
    let selectedDate = date;
    let selectedDateUnix = moment(date).unix();
    let minDateUnix = moment(scheduleData.minDate).unix();
    let startDate = scheduleData.start || scheduleData.minDate;
    let startDateUnix = startDate ? moment(startDate).unix() : 0;
    if(minDateUnix >= selectedDateUnix) {
      selectedDateUnix = minDateUnix + 600;
      selectedDate = new Date(selectedDateUnix * 1000);
    }
    if(selectedDateUnix < startDateUnix) {
      selectedDate = new Date((startDateUnix + 600)*1000);
    }
    if((!!manualActivation || !!autoActivation) && experience.status === 'Active') {
      startDateUnix = autoActivation > manualActivation ? autoActivation : manualActivation;
      startDate = new Date(startDateUnix * 1000);
    }
    let runtime:any = getDuration(startDate, selectedDate);
    setScheduleData({...scheduleData, end: selectedDate, runtime});
  }

  function showStartdate(option: boolean) {
    let myZone = moment.tz.guess().replace(/_/g, ' ');
    let myOffset = moment.tz(myZone).format('Z');
    myZone = myZone+' (UTC '+myOffset+')';
    if(option) {
      setScheduleData({
        ...scheduleData,
        start: new Date((moment().unix()+300)*1000),
        timezone: {key: 0, name: myZone, offset: parseInt(myOffset)},
        runtime: 'Ongoing'
      });
    } else {
      setScheduleData({
        ...scheduleData,
        start: null,
        end: null,
        timezone: {key: 0, name: '', offset: 0},
        runtime: 'None',
      });
      setEndDateExists(option);
    }
    setStartDateExists(option);
  }

  function showEndDate(option: boolean) {
    let endDate = scheduleData.end || new Date();
    let startDateUnix = moment(scheduleData.start || new Date()).unix();
    let endDateUnix = moment(endDate).unix();
    let myZone = moment.tz.guess().replace(/_/g, ' ');
    let myOffset = moment.tz(myZone).format('Z');
    myZone = myZone+' (UTC '+myOffset+')';

    if(startDateUnix >= endDateUnix) {
      endDate = new Date((startDateUnix + 300)*1000);
    } else {
      endDate = new Date((moment().unix() + 300)*1000);
    }
    let runtime: any = 'Ongoing';
    if(option) {
      runtime = getDuration(new Date((startDateUnix)*1000), endDate);
    }
    if(manualActivation && option) {
      runtime = getDuration(new Date((manualActivation)*1000), endDate);
      setScheduleData({
        ...scheduleData,
        timezone: {key: 0, name: myZone, offset: parseInt(myOffset)},
        end: option ? endDate : null,
        runtime: `${runtime}`
      });
    } else {
      setScheduleData({
        ...scheduleData,
        end: option ? endDate : null,
        runtime: `${runtime}`
      });
    }
    setEndDateExists(option);
  }

  function onCancel() {
    let myZone = moment.tz.guess().replace(/_/g, ' ');
    let myOffset = moment.tz(myZone).format('Z');
    myZone = myZone+' (UTC '+myOffset+')';
    setScheduleData({
      start: null,
      end: null,
      timezone: {
        key: 0,
        name: '',
        offset: 0,
      },
      runtime: 'None',
      minDate: new Date()
    });
    setStartDateExists(false);
    setEndDateExists(false);
    props.cancelButton?.onClick();
  }

  function renderAlreadyActivated() {
    if((!!manualActivation && experience.status === 'Active') ||
      (!!autoActivation && experience.status === 'Active')
    ) {
      const timezone = moment.tz.guess().replace(/_/g, ' ');
      const activationDate = autoActivation > manualActivation ? autoActivation : manualActivation;
      return (
        <div className="schedule-edit-modal__manual-activ">
          {autoActivation ? 'Automatically' : 'Manually'} launched {moment( activationDate * 1000).format('MMM D, YYYY hh:mmA')} ({timezone})
        </div>
      )
    }
  }

  useEffect(() => {
    let runtime: any = 'None';
    let startDate = scheduleState?.start ||
      ((!!manualActivation && experience.status === 'Active') || (autoActivation && experience.status === 'Active'))
      ? manualActivation : 0;
    let endDate = scheduleState?.end || 0;
    if(startDate) {
      runtime = 'Ongoing';
    }
    if(endDate) {
      runtime = getDuration(new Date(startDate*1000), new Date((endDate)*1000));
    }
    setTimezones(getTimezoneList());
    setScheduleData({
      ...scheduleData,
      start: scheduleState?.start ? new Date(scheduleState.start * 1000) : null,
      end: scheduleState?.end ? new Date(scheduleState.end * 1000) : null,
      timezone: {
        ...scheduleData.timezone,
        name: scheduleState?.timezone,
      },
      runtime,
      minDate: new Date(),
    });
    setStartDateExists(!!scheduleState?.start && experience.status !== 'Active');
    setEndDateExists(scheduleState?.end!!);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[scheduleState, props.isVisible]);


  return (
    <EditModal
      size="medium"
      icon=""
      title={props.title}
      isVisible={props.isVisible}
      primaryButton={{
        name: props.primaryButton.name,
        onClick: ()=>props.primaryButton.onClick(scheduleData),
        disabled: false,
      }}
      secondaryButton={props.secondaryButton ? {
        name: props.secondaryButton.name,
        onClick: () => props.secondaryButton?.onClick(scheduleData),
        disabled: false,
      } : undefined}
      cancelButton={props.cancelButton ? {
        name: props.cancelButton?.name,
        onClick: onCancel,
      } : undefined}
    >
      <div className="schedule-edit-modal">
        <div className="schedule-edit-modal__runtime">Experience Runtime: {scheduleData.runtime}</div>
          {startDateExists ? (
            <div className="schedule-edit-modal__start">
              <div className="schedule-edit-modal__label">Starts</div>
              <div className="schedule-edit-modal__start-date-box">
                <div className="schedule-edit-modal__datepicker">
                  <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <FlightDateTimePicker
                      format={dateFormat}
                      value={scheduleData.start}
                      //disablePast
                      onChange={(e:any)=>changeStartDate(e)}
                      showTodayButton
                      minutesStep={5}
                      minDate={scheduleData.minDate}
                    />
                  </MuiPickersUtilsProvider>
                </div>
                <FlightSelectSearchable
                  className="schedule-edit-modal__select"
                  label=""
                  options={timezones}
                  selected={scheduleData.timezone}
                  handleOptionClick={handleTimezoneSelect}
                  handleSearch={handleSearch}
                  dropdownMaxHeight="150px"
                />
                {!endDateExists && (
                  <FlightButton
                    className="schedule-edit-modal__remove-icon"
                    theme="minor"
                    onClick={()=>showStartdate(false)}
                    label="X"
                  />
                )}
              </div>
              <div className="schedule-edit-modal__comment">
                Experience will start at {convertStart.local_time} {convertStart.local_date} your local time.
              </div>
            </div>
          ) : (
            ((!manualActivation && !autoActivation) || experience.status !== 'Active') &&
              <FlightButton
                theme="link"
                className="schedule-edit-modal__add-end"
                onClick={()=>showStartdate(true)}
                label="+ Add start date"
              />
          )}
          {renderAlreadyActivated()}
          {endDateExists ? (
            <div className="schedule-edit-modal__end">
              <div className="schedule-edit-modal__label">Ends</div>
              <div className="schedule-edit-modal__start-date-box">
                <div className="schedule-edit-modal__datepicker">
                  <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <FlightDateTimePicker
                      format={dateFormat}
                      value={scheduleData.end}
                      //disablePast
                      onChange={(e:any)=>changeEndDate(e)}
                      showTodayButton
                      minutesStep={5}
                      minDate={scheduleData.minDate}
                    />
                  </MuiPickersUtilsProvider>
                </div>
                <FlightSelectSearchable
                  className="schedule-edit-modal__select"
                  label=""
                  options={timezones}
                  selected={scheduleData.timezone}
                  handleOptionClick={handleTimezoneSelect}
                  handleSearch={handleSearch}
                  dropdownMaxHeight="150px"
                  disabled={!manualActivation}
                />
                <FlightButton
                  className="schedule-edit-modal__remove-icon"
                  theme="minor"
                  onClick={()=>showEndDate(false)}
                  label="X"
                />
              </div>
              <div className="schedule-edit-modal__comment">
                Experience will end at {convertEnd.local_time} {convertEnd.local_date} your local time.
              </div>
            </div>
          ) : (
            (startDateExists || ((!!manualActivation || !!autoActivation) && experience.status === 'Active')) && (
              <FlightButton
                theme="link"
                className="schedule-edit-modal__add-end"
                onClick={()=>showEndDate(true)}
                label="+ Add end date"
              />
            )
          )}
      </div>
    </EditModal>
  )
}